import { LIBELLE_ROLE, cumulsSensiblesDe, type Role } from '@gsm/partage';
import { useAuth } from '../auth/contexte';
import { messageErreur, useRessource } from '../api/ressource';
import { Bloc } from '../composants/Tableau';
import { BandeauAlerte } from '../composants/BandeauAlerte';
import { Pastille } from '../composants/Pastille';
import { Nombre } from '../composants/Nombre';
import { Tuile, Tuiles } from '../composants/Tuile';
import { rolesDuChemin } from '../composants/Charpente';

interface Ventilation {
  /** Clé du filtre de l'écran Mandats : identifiant du profil, code d'agence. */
  cle: string;
  libelle: string;
  nb: number;
  encours: string;
}

interface Synthese {
  general: { mandatsActifs: number; actifNetSousGestion: string; ordresAValider: number };
  gerant: {
    aInvestir: number;
    mandatsEnDerive: number;
    mandatsARealigner: number;
    ordresSansDenouement: number;
  };
  middleBo: { situationsAttendues: number; lignesAVerifier: number };
  conformite: { profilPlusDe24Mois: number; mandatsSuspendus: number; mandatsSansPiece: number };
  direction: {
    collecteNette: string;
    versementsAnnee: string;
    retraitsAnnee: string;
    parProfil: Ventilation[];
    parAgence: Ventilation[];
  };
}

/** Montant de tuile : chiffre en didone, devise en appoint. */
function Montant({ valeur }: { valeur: string | undefined }) {
  return valeur === undefined ? (
    <Nombre valeur={null} />
  ) : (
    <span className="tuile__montant">
      <Nombre valeur={valeur} />
      <small>MAD</small>
    </span>
  );
}

const pluriel = (n: number, mot: string) => `${n} ${mot}${n > 1 ? 's' : ''}`;

/** Compteur de tuile : « — » tant que la synthèse n'est pas arrivée. */
const compte = (n: number | undefined) => n ?? <Nombre valeur={null} />;

export function Accueil() {
  const { profil, aRole } = useAuth();
  const roles = (profil?.roles ?? []) as Role[];
  const cumuls = cumulsSensiblesDe(roles);
  // Les tuiles sont posées d'emblée et se remplissent à l'arrivée de la
  // synthèse : tant qu'elle se calcule, l'écran ne doit pas paraître vide.
  const { donnees: s, erreur } = useRessource<Synthese>('/tableau-de-bord');

  const estGerant = aRole('GERANT', 'RESP_GESTION');
  const estMiddleBo = aRole('MIDDLE_BO', 'RESP_GESTION');
  const estConformite = aRole('CONFORMITE');
  const estDirection = aRole('DIRECTION');

  /** La tuile n'ouvre que l'écran que le rail ouvre aussi à ce compte. */
  const vers = (chemin: string) => {
    const requis = rolesDuChemin(chemin);
    return !requis || aRole(...requis) ? chemin : undefined;
  };

  return (
    <>
      <p className="entete__fil">Tableau de bord</p>
      <div className="entete">
        <h1>
          {profil?.prenom} {profil?.nom}
        </h1>
        <span className="entete__ref">{profil?.login}</span>
        {roles.map((role) => (
          <Pastille key={role} ton="interface">
            {LIBELLE_ROLE[role]}
          </Pastille>
        ))}
      </div>

      <Tuiles libelle="Chiffres clés">
        <Tuile
          titre="Mandats actifs"
          valeur={compte(s?.general.mandatsActifs)}
          lien={vers('/mandats?statut=ACTIF')}
        />
        <Tuile
          titre="Actif net sous gestion"
          valeur={<Montant valeur={s?.general.actifNetSousGestion} />}
          lien={vers('/tresorerie')}
        />
        <Tuile
          titre="Ordres à valider"
          valeur={compte(s?.general.ordresAValider)}
          lien={vers('/ordres?statut=A_VALIDER')}
          alerte={(s?.general.ordresAValider ?? 0) > 0}
        />
      </Tuiles>

      {erreur && (
        <BandeauAlerte nature="gestion" titre="Tableau de bord indisponible">
          {messageErreur(erreur)}
        </BandeauAlerte>
      )}

      {cumuls.length > 0 && (
        <BandeauAlerte nature="gestion" titre="Cumul de rôles signalé">
          Ce compte cumule&nbsp;
          {cumuls.map(([a, b]) => `${LIBELLE_ROLE[a]} et ${LIBELLE_ROLE[b]}`).join(', ')}. Le
          principe des quatre yeux reste appliqué opération par opération, mais ce cumul est remonté
          à la conformité.
        </BandeauAlerte>
      )}

      {estGerant && (
        <Bloc titre="Gestion" meta="à l’attention du gérant">
          <Tuiles>
            <Tuile
              titre="À investir"
              valeur={compte(s?.gerant.aInvestir)}
              lien={vers('/tresorerie?oisifs=1')}
              alerte={(s?.gerant.aInvestir ?? 0) > 0}
            />
            <Tuile
              titre="Mandats en dérive"
              valeur={compte(s?.gerant.mandatsEnDerive)}
              lien={vers('/campagnes')}
              alerte={(s?.gerant.mandatsEnDerive ?? 0) > 0}
            />
            <Tuile
              titre="À réaligner"
              valeur={compte(s?.gerant.mandatsARealigner)}
              lien={vers('/campagnes')}
              alerte={(s?.gerant.mandatsARealigner ?? 0) > 0}
            />
            <Tuile
              titre="Ordres sans dénouement"
              valeur={compte(s?.gerant.ordresSansDenouement)}
              lien={vers('/ordres?statut=TRANSMIS')}
              alerte={(s?.gerant.ordresSansDenouement ?? 0) > 0}
            />
          </Tuiles>
        </Bloc>
      )}

      {estMiddleBo && (
        <Bloc titre="Back-office" meta="ce que le tiers doit encore envoyer">
          <Tuiles>
            <Tuile
              titre="Situations attendues"
              valeur={compte(s?.middleBo.situationsAttendues)}
              lien={vers('/import-situation')}
              alerte={(s?.middleBo.situationsAttendues ?? 0) > 0}
            />
            <Tuile
              titre="Lignes BDT à vérifier"
              valeur={compte(s?.middleBo.lignesAVerifier)}
              lien={vers('/referentiels/instruments?statut=A_COMPLETER')}
              alerte={(s?.middleBo.lignesAVerifier ?? 0) > 0}
            />
          </Tuiles>
        </Bloc>
      )}

      {estConformite && (
        <Bloc titre="Conformité" meta="points de vigilance">
          <Tuiles>
            <Tuile
              titre="Profil déterminé il y a plus de 24 mois"
              valeur={compte(s?.conformite.profilPlusDe24Mois)}
              lien={vers('/mandats?statut=ACTIF&vigilance=profil-ancien')}
            />
            <Tuile
              titre="Mandats suspendus"
              valeur={compte(s?.conformite.mandatsSuspendus)}
              lien={vers('/mandats?statut=SUSPENDU')}
            />
            <Tuile
              titre="Mandats sans mandat scanné"
              valeur={compte(s?.conformite.mandatsSansPiece)}
              lien={vers('/mandats?statut=ACTIF&vigilance=sans-piece')}
              alerte={(s?.conformite.mandatsSansPiece ?? 0) > 0}
            />
          </Tuiles>
        </Bloc>
      )}

      {estDirection && (
        <>
          <Bloc titre="Direction" meta="collecte de l’année en cours">
            <Tuiles>
              <Tuile
                titre="Collecte nette"
                valeur={<Montant valeur={s?.direction.collecteNette} />}
                lien={vers('/reporting')}
              />
              <Tuile
                titre="Versements"
                valeur={<Montant valeur={s?.direction.versementsAnnee} />}
                lien={vers('/reporting')}
              />
              <Tuile
                titre="Retraits"
                valeur={<Montant valeur={s?.direction.retraitsAnnee} />}
                lien={vers('/reporting')}
              />
            </Tuiles>
          </Bloc>

          {/* Ventilations : leur nombre de tuiles dépend des données. */}
          {s && (
            <>
              <Bloc titre="Encours par profil">
                <Tuiles>
                  {s.direction.parProfil.map((v) => (
                    <Tuile
                      key={v.cle}
                      titre={v.libelle}
                      valeur={<Montant valeur={v.encours} />}
                      appoint={pluriel(v.nb, 'mandat')}
                      lien={vers(`/mandats?statut=ACTIF&profil=${encodeURIComponent(v.cle)}`)}
                    />
                  ))}
                </Tuiles>
              </Bloc>

              <Bloc titre="Encours par agence de rattachement">
                <Tuiles>
                  {s.direction.parAgence.map((v) => (
                    <Tuile
                      key={v.cle}
                      titre={v.libelle}
                      valeur={<Montant valeur={v.encours} />}
                      appoint={pluriel(v.nb, 'mandat')}
                      lien={vers(`/mandats?statut=ACTIF&agence=${encodeURIComponent(v.cle)}`)}
                    />
                  ))}
                </Tuiles>
              </Bloc>
            </>
          )}
        </>
      )}
    </>
  );
}
